import { useEffect } from "react";
import { api } from "@/lib/api";

const MARKER = "data-mds-injected";

// Module-level guard so React StrictMode double-mount doesn't duplicate injections.
let inFlight = null;
let currentNodes = [];

function clearInjected() {
  currentNodes.forEach((n) => { try { n.remove(); } catch {} });
  currentNodes = [];
  document.head.querySelectorAll(`[${MARKER}]`).forEach((n) => n.remove());
}

function scriptAlreadyPresent(src) {
  if (!src) return false;
  return !!document.head.querySelector(`script[src="${src}"]`);
}

function metaAlreadyPresent(name, content) {
  if (!name) return false;
  const existing = document.head.querySelector(`meta[name="${name}"]`);
  return !!existing && (!content || existing.getAttribute("content") === content);
}

function inject(html) {
  clearInjected();
  if (!html || !html.trim()) return;
  const container = document.createElement("div");
  container.innerHTML = html;

  container.querySelectorAll("script").forEach((orig) => {
    const src = orig.getAttribute("src");
    // Skip if the exact same script is already present in <head> (e.g. hard-coded in index.html)
    if (src && scriptAlreadyPresent(src)) return;

    const s = document.createElement("script");
    for (const attr of Array.from(orig.attributes)) s.setAttribute(attr.name, attr.value);
    if (orig.textContent) s.textContent = orig.textContent;
    s.setAttribute(MARKER, "script");
    document.head.appendChild(s);
    currentNodes.push(s);
  });

  container.querySelectorAll("meta").forEach((orig) => {
    const name = orig.getAttribute("name");
    const content = orig.getAttribute("content");
    if (metaAlreadyPresent(name, content)) return;

    const m = document.createElement("meta");
    for (const attr of Array.from(orig.attributes)) m.setAttribute(attr.name, attr.value);
    m.setAttribute(MARKER, "meta");
    document.head.appendChild(m);
    currentNodes.push(m);
  });
}

export function AdsenseInjector() {
  useEffect(() => {
    if (!inFlight) {
      inFlight = api
        .get("/settings")
        .then(({ data }) => inject(data.adsense_head_html || ""))
        .catch(() => {})
        .finally(() => { inFlight = null; });
    }
  }, []);
  return null;
}
